import { SimulatorType } from "@/types/api/SimulatorType"
import { LANGUAGE_TYPES } from "@/types/ModelConfigs/LanguageTypes"
import type { RequiredKey } from "@/types/SimulatorConfig"

export const GEMINI_REALTIME_MODELS = [
  {
    value: "models/gemini-3.1-flash-live-preview",
    label: "Gemini 3.1 Flash Live (Preview)",
  },
  {
    value: "models/gemini-2.0-flash-live-001",
    label: "Gemini 2.0 Flash Live",
  },
]

export const GEMINI_REALTIME_VOICES = [
  { value: "Puck", label: "Puck", description: "Upbeat, neutral" },
  { value: "Charon", label: "Charon", description: "Neutral, balanced" },
  { value: "Kore", label: "Kore", description: "Warm, female" },
  { value: "Fenrir", label: "Fenrir", description: "Deep, male" },
  { value: "Aoede", label: "Aoede", description: "Bright, female" },
  { value: "Leda", label: "Leda", description: "Clear, female" },
  { value: "Orus", label: "Orus", description: "Authoritative, male" },
  { value: "Zephyr", label: "Zephyr", description: "Breezy, neutral" },
]

export const GEMINI_REALTIME_DEFAULT_MODEL = GEMINI_REALTIME_MODELS[0].value
export const GEMINI_REALTIME_DEFAULT_VOICE = "Charon"

export const GROK_REALTIME_MODELS = [
  {
    value: "grok-voice-think-fast-1.0",
    label: "Grok Voice Think Fast 1.0",
  },
  {
    value: "grok-voice-latest",
    label: "Grok Voice (Latest)",
  },
]

export const GROK_REALTIME_VOICES = [
  { value: "eve", label: "Eve", description: "Energetic, upbeat, female" },
  { value: "ara", label: "Ara", description: "Warm, friendly, female" },
  { value: "rex", label: "Rex", description: "Confident, clear, male" },
  { value: "sal", label: "Sal", description: "Smooth, balanced, neutral" },
  { value: "leo", label: "Leo", description: "Authoritative, strong, male" },
]

export const GROK_REALTIME_DEFAULT_MODEL = GROK_REALTIME_MODELS[0].value
export const GROK_REALTIME_DEFAULT_VOICE = "ara"

export const OPENAI_REALTIME_MODELS = [
  { value: "gpt-realtime-2", label: "GPT Realtime 2" },
  { value: "gpt-live-1", label: "GPT Live 1" },
]

export const OPENAI_GPT_LIVE_ALPHA_MODELS = [
  {
    value: "gpt-live-1-marble-alpha",
    label: "GPT Live 1 Marble (Private Alpha)",
  },
]

export const OPENAI_REALTIME_VOICES = [
  { value: "alloy", label: "Alloy", description: "Neutral, balanced" },
  { value: "ash", label: "Ash", description: "Authoritative, male" },
  { value: "ballad", label: "Ballad", description: "Warm, male" },
  { value: "coral", label: "Coral", description: "Friendly, female" },
  { value: "echo", label: "Echo", description: "Deep, male" },
  { value: "marin", label: "Marin", description: "Natural, expressive" },
  { value: "sage", label: "Sage", description: "Calm, female" },
  { value: "shimmer", label: "Shimmer", description: "Bright, female" },
  { value: "verse", label: "Verse", description: "Professional, female" },
]

// Voices shared by public and private Live models, but unsupported by Realtime.
export const OPENAI_GPT_LIVE_ALPHA_ONLY_VOICE_VALUES = new Set(["marin"])

export const OPENAI_REALTIME_DEFAULT_MODEL = OPENAI_REALTIME_MODELS[0].value
export const OPENAI_REALTIME_DEFAULT_VOICE = "alloy"

export const OPENAI_REALTIME_API_KEY_SOURCE = {
  CUSTOMER_PROVIDED: "customer_provided",
  COVAL_MANAGED: "coval_managed",
} as const

export type OpenAIRealtimeApiKeySource =
  (typeof OPENAI_REALTIME_API_KEY_SOURCE)[keyof typeof OPENAI_REALTIME_API_KEY_SOURCE]

export function isOpenAIRealtimeApiKeySource(
  value: unknown,
): value is OpenAIRealtimeApiKeySource {
  return Object.values(OPENAI_REALTIME_API_KEY_SOURCE).some(
    (source) => source === value,
  )
}

/** The public GPT Live model plus the private alpha Live models. */
export const isOpenAIRealtimeModelLive = (model: string | undefined): boolean =>
  model === "gpt-live-1" ||
  OPENAI_GPT_LIVE_ALPHA_MODELS.some(
    (modelOption) => modelOption.value === model,
  )

/**
 * The voice that must accompany the OpenAI Realtime configuration when its
 * model changes to `newModel`, given the model/voice being replaced — or
 * undefined to keep the current voice. Live-only voices (e.g. Marin) are
 * rejected by the GA Realtime API, and entering public Live selects the
 * shared Live voice, so a model change carries the paired voice correction.
 * Single source of truth shared by the agent form
 * (createOpenAIRealtimeModelUpdate) and the agent-mutations override editor.
 */
export function getPairedOpenAIRealtimeVoice(
  newModel: string,
  currentModel: string | undefined,
  currentVoice: string | undefined,
): string | undefined {
  if (
    !isOpenAIRealtimeModelLive(newModel) &&
    currentVoice !== undefined &&
    OPENAI_GPT_LIVE_ALPHA_ONLY_VOICE_VALUES.has(currentVoice)
  ) {
    return OPENAI_REALTIME_DEFAULT_VOICE
  }
  if (newModel === "gpt-live-1" && !isOpenAIRealtimeModelLive(currentModel)) {
    return "marin"
  }
  return undefined
}

/**
 * Info-tooltip copy for Pipecat Cloud agent arguments. Single source of truth
 * shared by the dedicated Pipecat form fields (AgentPreviewSection) and the
 * SIMULATOR_FIELDS entries below.
 */
export const PIPECAT_TOOLTIPS = {
  apiKey:
    "Authentication key that allows Coval to connect to your Pipecat Cloud agent. Stored encrypted and handled securely.",
  agentName:
    "The name of the Pipecat Cloud agent Coval will call during simulations. Must exactly match the agent name in your Pipecat Cloud dashboard.",
  customData:
    "Additional context passed to your Pipecat agent at the start of each simulation. Must be a valid JSON string.",
} as const

export const SIMULATOR_FIELDS: Record<SimulatorType, RequiredKey[]> = {
  [SimulatorType.VOICE]: [
    {
      key: "media_encryption",
      label: "Media Encryption",
      help: "Requests encrypted media (SRTP or DTLS) when Coval dials a SIP address. SBCs that enforce secure media require this setting.",
      optional: true,
      default_value: "Disabled",
      options: ["Disabled", "SRTP", "DTLS"],
      option_values: {
        Disabled: "disabled",
        SRTP: "SRTP",
        DTLS: "DTLS",
      },
    },
    {
      key: "custom_sip_headers",
      label: "Custom SIP Headers",
      help: "JSON object of custom headers sent on the outgoing SIP INVITE. Each key-value pair becomes a separate header. X-Coval-Simulation-Id is always included automatically.",
      optional: true,
    },
  ],
  [SimulatorType.OUTBOUND_VOICE]: [],
  [SimulatorType.TEXT]: [],
  [SimulatorType.CHAT_A2A]: [
    {
      key: "chat_endpoint",
      label: "A2A Endpoint",
      help: "The A2A JSON-RPC endpoint URL for message/send requests.",
      tooltip:
        "Where Coval POSTs each turn as an A2A v2 JSON-RPC message/send request. Must be a publicly reachable HTTPS URL.",
      optional: false,
    },
    {
      key: "initialization_endpoint",
      label: "OAuth Token Endpoint",
      help: "OAuth2 token endpoint URL for client credentials authentication.",
      tooltip:
        "Optional. Coval calls this once at the start of each simulation to fetch an OAuth2 token, then sends it as 'Authorization: Bearer …' on every chat request. Leave blank to skip authentication.",
      optional: true,
    },
    {
      key: "initialization_payload",
      label: "OAuth Payload",
      help: 'JSON payload for OAuth2 token request. e.g. {"grant_type":"client_credentials","client_id":"...","client_secret":"..."}',
      tooltip:
        "Optional. Body sent to the token endpoint, e.g. a client_credentials grant. Encoded per the initialization content-type (form-encoded by default).",
      optional: true,
    },
  ],
  [SimulatorType.SMS]: [],
  // Keep empty so renderRequiredKeys() returns null and the
  // <WebSocketSimulatorConfig> toggle can mount. This fixes the
  // regression where non-empty fields caused
  // renderRequiredKeys() to short-circuit the toggle rendering.
  [SimulatorType.WEBSOCKET]: [],
  // Same reasoning as SimulatorType.WEBSOCKET: keep empty to allow the
  // Direct vs HTTP toggle to render for chat websocket agents.
  [SimulatorType.CHAT_WEBSOCKET]: [],
  [SimulatorType.LIVEKIT]: [],
  [SimulatorType.PIPECAT]: [
    {
      key: "pipecat_api_key",
      optional: false,
      tooltip: PIPECAT_TOOLTIPS.apiKey,
    },
    {
      key: "agent_name",
      optional: false,
      tooltip: PIPECAT_TOOLTIPS.agentName,
    },
  ],
  [SimulatorType.NOOP]: [],
  [SimulatorType.STT]: [
    {
      key: "provider",
      label: "Provider",
      help: "The speech-to-text service provider to use for transcribing audio. Different providers may have varying accuracy and features.",
      optional: false,
    },
    {
      key: "model",
      label: "Model",
      help: "The specific STT model to use from the selected provider. For example, 'whisper-1' for OpenAI or provider-specific model names.",
      optional: false,
    },
    {
      key: "language",
      label: "Language",
      help: "The primary language expected in the audio input. This helps the STT engine optimize transcription accuracy for the specified language.",
      optional: false,
      options: LANGUAGE_TYPES.map((lang) => lang.name),
      option_values: Object.fromEntries(
        LANGUAGE_TYPES.map((lang) => [lang.name, lang.id]),
      ),
      default_value: "English",
    },
  ],
  [SimulatorType.REALTIME_VOICE_TO_VOICE]: [],
  [SimulatorType.GEMINI_REALTIME]: [
    {
      key: "gemini_realtime_api_key",
      label: "Google AI API Key",
      help: "Your Google AI Studio (or Express Mode) key with Gemini Live access enabled. Supports both 'AIzaSy…' (AI Studio) and 'AQ.…' (Express Mode) formats.",
      optional: false,
    },
    {
      key: "gemini_realtime_instructions",
      label: "Agent System Instructions",
      help: "Defines how your Gemini Live agent should behave during conversations. Sent as the setup system_instruction. Leave empty to use model defaults.",
      optional: true,
    },
    {
      key: "gemini_realtime_model",
      label: "Model",
      help: "The Gemini Live model to use. gemini-3.1-flash-live-preview is Google's current Live replacement; gemini-2.0-flash-live-001 is the stable baseline.",
      optional: false,
      default_value: GEMINI_REALTIME_MODELS[0].label,
      options: GEMINI_REALTIME_MODELS.map((m) => m.label),
      option_values: Object.fromEntries(
        GEMINI_REALTIME_MODELS.map((m) => [m.label, m.value]),
      ),
    },
    {
      key: "gemini_realtime_voice",
      label: "Agent Voice",
      help: "The prebuilt voice used by the Gemini Live agent. Options: Puck, Charon (neutral), Kore, Fenrir, Aoede, Leda, Orus, Zephyr.",
      optional: false,
      default_value: GEMINI_REALTIME_DEFAULT_VOICE,
      options: GEMINI_REALTIME_VOICES.map((v) => v.label),
      option_values: Object.fromEntries(
        GEMINI_REALTIME_VOICES.map((v) => [v.label, v.value]),
      ),
    },
    {
      key: "gemini_realtime_temperature",
      label: "Temperature",
      help: "Controls randomness in responses. Range: 0.0 (deterministic) to 2.0 (very random). Default: 0.8.",
      optional: true,
    },
    {
      key: "simulation_timeout_seconds",
      label: "Simulation Timeout (seconds)",
      help: "Maximum duration for a simulation in seconds. Range: 1-1800 (30 minutes). Default: 900 (15 minutes).",
      optional: true,
    },
  ],
  [SimulatorType.OPENAI_REALTIME]: [
    {
      key: "openai_realtime_api_key_source",
      label: "OpenAI API Key Source",
      help: "Choose whether simulations use Coval's managed OpenAI credential or an API key you provide.",
      optional: true,
      overridable: false,
      default_value: OPENAI_REALTIME_API_KEY_SOURCE.COVAL_MANAGED,
      options: ["Coval provided", "Customer provided"],
      option_values: {
        "Coval provided": OPENAI_REALTIME_API_KEY_SOURCE.COVAL_MANAGED,
        "Customer provided": OPENAI_REALTIME_API_KEY_SOURCE.CUSTOMER_PROVIDED,
      },
    },
    {
      key: "openai_realtime_api_key",
      label: "OpenAI API Key",
      help: "Your OpenAI API key for accessing the Realtime API. Must start with 'sk-' and have realtime API access enabled.",
      optional: true,
    },
    {
      key: "openai_realtime_instructions",
      label: "Agent System Instructions",
      help: "Defines how your OpenAI Realtime agent should behave during conversations. This is passed directly to OpenAI as your agent's system prompt. Leave empty to use default instructions.",
      optional: true,
    },
    {
      key: "openai_realtime_model",
      label: "Model",
      help: "The OpenAI Realtime model to use.",
      optional: false,
      default_value: "gpt-realtime-2",
      options: OPENAI_REALTIME_MODELS.map((model) => model.label),
      option_values: Object.fromEntries(
        OPENAI_REALTIME_MODELS.map((model) => [model.label, model.value]),
      ),
    },
    {
      key: "openai_realtime_voice",
      label: "Agent Voice",
      help: "The voice used by the OpenAI Realtime agent. Options: Alloy (neutral), Ash (authoritative), Ballad (warm), Coral (friendly), Echo (deep), Marin (Live models only), Sage (calm), Shimmer (bright), Verse (professional).",
      optional: false,
      default_value: "alloy",
      options: OPENAI_REALTIME_VOICES.map((voice) => voice.label),
      option_values: Object.fromEntries(
        OPENAI_REALTIME_VOICES.map((voice) => [voice.label, voice.value]),
      ),
    },
    {
      key: "openai_realtime_temperature",
      label: "Temperature",
      help: "Controls randomness in responses. Range: 0.0 (deterministic) to 2.0 (very random). Default: 0.8.",
      optional: true,
    },
    {
      key: "simulation_timeout_seconds",
      label: "Simulation Timeout (seconds)",
      help: "Maximum duration for a simulation in seconds. Range: 1-1800 (30 minutes). Default: 900 (15 minutes).",
      optional: true,
    },
  ],
  // NOTE: no temperature field — the Grok Voice Agent API does not expose a
  // temperature knob in its session config (generation tuning is
  // instructions-only).
  [SimulatorType.GROK_REALTIME]: [
    {
      key: "grok_realtime_api_key",
      label: "xAI API Key",
      help: "Your xAI API key with Grok Voice Agent API access. Starts with 'xai-'.",
      optional: false,
    },
    {
      key: "grok_realtime_instructions",
      label: "Agent System Instructions",
      help: "Defines how your Grok agent should behave during conversations. Sent as the session instructions. Leave empty to use model defaults.",
      optional: true,
    },
    {
      key: "grok_realtime_model",
      label: "Model",
      help: "The Grok voice model to use. grok-voice-think-fast-1.0 is xAI's flagship voice model; grok-voice-latest always points to the newest.",
      optional: false,
      default_value: GROK_REALTIME_MODELS[0].label,
      options: GROK_REALTIME_MODELS.map((m) => m.label),
      option_values: Object.fromEntries(
        GROK_REALTIME_MODELS.map((m) => [m.label, m.value]),
      ),
    },
    {
      key: "grok_realtime_voice",
      label: "Agent Voice",
      help: "The prebuilt voice used by the Grok agent. Options: Eve (energetic), Ara (warm), Rex (confident), Sal (smooth), Leo (authoritative).",
      optional: false,
      default_value: GROK_REALTIME_DEFAULT_VOICE,
      options: GROK_REALTIME_VOICES.map((v) => v.label),
      option_values: Object.fromEntries(
        GROK_REALTIME_VOICES.map((v) => [v.label, v.value]),
      ),
    },
    {
      key: "simulation_timeout_seconds",
      label: "Simulation Timeout (seconds)",
      help: "Maximum duration for a simulation in seconds. Range: 1-1800 (30 minutes). Default: 900 (15 minutes).",
      optional: true,
    },
  ],
  // NOTE: no model/voice fields — Deepgram's Voice Agent API takes a full
  // Settings message (listen/think/speak providers, greeting, audio rates)
  // rather than single model/voice knobs, so those live in the free-form
  // deepgram_realtime_settings JSON.
  [SimulatorType.DEEPGRAM_REALTIME]: [
    {
      key: "deepgram_realtime_api_key",
      label: "Deepgram API Key",
      help: "Your Deepgram API key with Voice Agent API access. Create one in the Deepgram Console.",
      optional: false,
    },
    {
      key: "deepgram_realtime_prompt",
      label: "Agent System Prompt",
      help: "Defines how your Deepgram agent should behave during conversations. Sent as agent.think.prompt in the Settings message. Leave empty to use the prompt in the Settings override.",
      optional: true,
    },
    {
      key: "deepgram_realtime_functions",
      label: "Agent Tool Definitions",
      help: 'JSON array of tools the agent may call, sent as agent.think.functions. e.g. [{"name": "get_weather", "description": "Get the weather", "parameters": {"type": "object"}}]',
      optional: true,
    },
    {
      key: "deepgram_realtime_settings",
      label: "Deepgram Settings Override",
      help: 'JSON object deep-merged into the Settings initialization message. Use it to set the STT model (agent.listen.provider.model, e.g. "nova-3" or "flux-general-en"), think provider/model, speak voice, agent.greeting, and audio sample rates.',
      optional: true,
    },
    {
      key: "simulation_timeout_seconds",
      label: "Simulation Timeout (seconds)",
      help: "Maximum duration for a simulation in seconds. Range: 1-1800 (30 minutes). Default: 900 (15 minutes).",
      optional: true,
    },
  ],
}
